import { IDirectDebit } from "@/models/directDebit/directDebit.types"
import { DirectDebitParser } from "@/models/directDebit/directDebit.parser"
import { QueryResult, MutationResult } from "@/api/utils/types"
import { useQuery, useMutation } from "@/api/utils/useQuery"
import { BackofficeRestQueries } from "@/api/backofficeRest/queries"
import {
  BackofficeRestMutations,
  CreateDirectDebitPayload,
} from "@/api/backofficeRest/mutations"
import { mapQueryResult } from "@/api/utils/mapQuery"
import { AggregatedQueryProps } from "@/api/utils/QueryAggregator"
import {
  getDirectDebitDefaultMandate,
  formatSortCode,
} from "@/helpers/directDebitHelper"
import {
  CreateDirectDebitResponse,
  DirectDebitMandate,
  PaymentMethodVerificationRequest,
  PaymentMethodVerificationResponse,
} from "@/api/backofficeRest/generated/model"

export interface IVerifyPaymentMethodInput {
  bankAccountNumber: string
  sortCode: string
}

export interface ISetUpDirectDebitInput {
  accountId: number
  bankAccountName: string
  bankAccountNumber: string
  bankSortCode: string
}

export interface IDirectDebitService {
  getForAccount(billingAccountId: number | null): QueryResult<IDirectDebit[]>
  hasActiveDirectDebit: (
    billingAccountId: number | null,
  ) => AggregatedQueryProps<boolean>
}

class DirectDebitServiceClass implements IDirectDebitService {
  getForAccount(billingAccountId: number | null): QueryResult<IDirectDebit[]> {
    return useQuery({
      ...BackofficeRestQueries.payments.directDebitMandates(
        billingAccountId ?? 0,
      ),
      enabled: !!billingAccountId,
      placeholderData: [],
      useRawResponse: true,
      parserFn: (rawData: DirectDebitMandate[]) =>
        (rawData ?? []).map((item) => DirectDebitParser.toDomain(item)),
    })
  }

  hasActiveDirectDebit(
    billingAccountId: number | null,
  ): AggregatedQueryProps<boolean> {
    return mapQueryResult(this.getForAccount(billingAccountId), (query) => {
      if (!query.data?.length) return false

      return Boolean(getDirectDebitDefaultMandate(query.data)?.isActive)
    })
  }

  setUpDirectDebit(): MutationResult<
    ISetUpDirectDebitInput,
    CreateDirectDebitResponse
  > {
    return useMutation({
      ...BackofficeRestMutations.payments.createDirectDebit(),
      serializerFn: (
        data: ISetUpDirectDebitInput,
      ): CreateDirectDebitPayload => ({
        accountId: data.accountId,
        body: {
          bank_account_name: data.bankAccountName,
          bank_account_number: data.bankAccountNumber,
          bank_sort_code: data.bankSortCode,
        },
      }),
    })
  }

  verifyPaymentMethod(): MutationResult<
    IVerifyPaymentMethodInput,
    PaymentMethodVerificationResponse
  > {
    return useMutation({
      ...BackofficeRestMutations.payments.verifyPaymentMethod(),
      serializerFn: (
        data: IVerifyPaymentMethodInput,
      ): PaymentMethodVerificationRequest => ({
        bank_account_number: data.bankAccountNumber,
        sort_code: formatSortCode(data.sortCode),
      }),
      invalidateCache: false,
    })
  }
}

export const DirectDebitService = new DirectDebitServiceClass()
